import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { Announcement, Audience, OperatorState, PresentationDetail, PresentationItemInput } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { KIND_LABEL } from './PresentationBuilderPage';
import { CountdownView, presentationFileUrl, SlideView, useCountdown } from './SlideView';
import { usePolling } from './usePolling';
import { usePresentationSocket } from './usePresentationSocket';
import { ObsLinkCard } from './ObsOutput';
import { ProjectButtons } from './ProjectButtons';
import { QrCode } from '../nursery/QrCode';
import { ConsoleLibrary, DRAG_TYPE, type BackgroundPick, type NewItem } from './ConsoleLibrary';
import { Splitter, useConsoleSizes } from './Splitter';
import { useContextMenu } from './ContextMenu';
import { AvisosPanel } from './AvisosPanel';
import { ThemeEditor } from './ThemePage';
import { CannotOperate } from './Lapsed';
import { isVideoSlide, PlaybackBar } from './Player';
import { AssistBanner, RemoteInvite } from './RemoteHelp';
import { loadSources } from './sources';
import { importedPassages, orderParts, rememberPassages } from './orderPassages';

type Command = Record<string, unknown> & { action: string };

/** Las dos primeras líneas bastan para distinguir estrofas que empiezan igual. */
const firstLines = (text: string | null) => (text ? text.split('\n').slice(0, 2).join('\n') : null);

/**
 * Consola del operador, acomodada como EasyWorship: arriba el programa, las diapositivas del
 * elemento elegido y los monitores «Siguiente» y «En pantalla»; abajo la biblioteca (Cantos, Biblia, Multimedia). Teclado: → o
 * espacio siguiente, ← anterior, Enter mostrar, B negro, L logo, C ocultar letra.
 */
export function ConsolePage() {
  const { presentationId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const canOperate = me.can('presentation.operate');
  const base = `/presentation/churches/${churchId}/presentations/${presentationId}`;
  // Si otro operador (o el celular) cambia algo, se ve al instante.
  const refresh = useRef(() => {});
  const instant = usePresentationSocket(churchId ? { churchId, presentationId } : null, () => refresh.current());
  const state = usePolling((signal) => api.get<OperatorState>(`${base}/live`, signal), instant ? 5000 : 1500, [base]);
  refresh.current = state.refresh;
  const detail = useAsync((signal) => (churchId ? api.get<PresentationDetail>(base, signal) : Promise.reject(new Error('Sin contexto'))), [base]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = useCallback(
    async (command: Command) => {
      setBusy(command.action);
      setError(null);
      try {
        state.setData(await api.post<OperatorState>(`${base}/control`, command));
      } catch (e) {
        setError(errorMessage(e));
      } finally {
        setBusy(null);
      }
    },
    [base, state],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target;
      if ((target instanceof Element && target.closest('input, textarea, select, button, a')) || e.altKey || e.ctrlKey || e.metaKey) return;
      const map: Record<string, Command> = {
        ArrowRight: { action: 'next' },
        ' ': { action: 'next' },
        ArrowLeft: { action: 'previous' },
        Enter: { action: 'take' },
      };
      const key = e.key.toLowerCase();
      const command = map[e.key] ?? (key === 'b' ? { action: 'blackout', on: !state.data?.blackout } : key === 'l' ? { action: 'logo', on: !state.data?.logo } : key === 'c' ? { action: 'textHidden', on: !state.data?.textHidden } : null);
      if (!command) return;
      e.preventDefault();
      void send(command);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [send, state.data?.blackout, state.data?.logo, state.data?.textHidden]);

  // Elemento del programa cuyas diapositivas se ven en el centro (el de «Siguiente», si no se elige otro).
  const [chosen, setChosen] = useState<number | null>(null);
  const [saved, setSaved] = useState<PresentationDetail | null>(null);
  const [dropping, setDropping] = useState(false);
  const sizes = useConsoleSizes();
  // Tiempo y duración del video en vivo (los da el reproductor del monitor «En pantalla»).
  const [videoInfo, setVideoInfo] = useState<{ current: number; duration: number } | null>(null);
  const menu = useContextMenu();
  const [slideView, setSlideView] = useSlideView();
  const [themeOpen, setThemeOpen] = useState(false);
  const navigate = useNavigate();
  // Funciones fijas para la biblioteca de abajo: así no se vuelve a pintar con cada consulta del estado en vivo.
  const addRef = useRef<(item: NewItem | NewItem[]) => Promise<void>>(async () => {});
  const backgroundRef = useRef<(pick: BackgroundPick) => Promise<void>>(async () => {});
  const onAdd = useCallback((item: NewItem | NewItem[]) => addRef.current(item), []);
  const onBackground = useCallback((pick: BackgroundPick) => backgroundRef.current(pick), []);
  const scriptureRef = useRef<(chapter: string, translation: string | null, slide: number) => Promise<void>>(async () => {});
  const onScripture = useCallback((chapter: string, translation: string | null, slide: number) => scriptureRef.current(chapter, translation, slide), []);

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  if (!canOperate) return <CannotOperate churchId={churchId} fallback="Solo quien opera presentaciones puede usar la consola." />;

  const s = state.data;
  const fileUrl = (rid: string) => presentationFileUrl(churchId, presentationId, rid);
  const p = saved ?? (detail.status === 'ready' ? detail.data : null);
  const items = p?.itemsList ?? [];
  const selectedPos = chosen ?? s?.previewCursor?.item ?? s?.liveCursor?.item ?? items[0]?.position ?? null;
  const selected = items.find((i) => i.position === selectedPos) ?? items[0] ?? null;

  /**
   * Todo se proyecta desde Programa: lo que se arrastra (o se agrega) de abajo queda al final del
   * programa y elegido, con sus diapositivas a la vista para proyectarlas.
   */
  const input = (i: PresentationItemInput): PresentationItemInput => ({
    kind: i.kind,
    title: i.title,
    body: i.body ?? null,
    passage: i.passage ?? null,
    resourceId: i.resourceId ?? null,
    stageNotes: i.stageNotes ?? null,
    arrangement: i.arrangement ?? null,
    backgroundPreset: i.backgroundPreset ?? null,
    localFile: i.localFile ?? null,
    youtube: i.youtube ?? null,
    translation: i.translation ?? null,
    camera: i.camera ?? null,
  });

  /** Uno o varios (los seleccionados de Multimedia, en su orden) al final del programa. */
  async function addItem(item: NewItem | NewItem[]) {
    if (!p) throw new Error('Espera a que cargue el programa.');
    const added = (Array.isArray(item) ? item : [item]).map((i) => input(i as PresentationItemInput));
    const next = await api.put<PresentationDetail>(base, { title: p.title, serviceId: p.service?.id ?? null, items: [...p.itemsList.map(input), ...added] });
    setSaved(next);
    const first = next.itemsList[next.itemsList.length - added.length];
    if (first) setChosen(first.position);
    state.refresh();
  }

  /** Clic derecho en Programa → «Quitar del programa». */
  async function removeItem(position: number) {
    if (!p) return;
    setError(null);
    try {
      const next = await api.put<PresentationDetail>(base, { title: p.title, serviceId: p.service?.id ?? null, items: p.itemsList.filter((i) => i.position !== position).map(input) });
      setSaved(next);
      setChosen(null);
      state.refresh();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  /** Doble clic en un video o imagen de Multimedia: queda detrás de la letra al instante. */
  async function setBackground(pick: BackgroundPick | null) {
    await send({ action: 'background', resourceId: pick?.resourceId ?? null, localFile: pick?.localFile ?? null, camera: pick?.camera ?? null });
  }

  /**
   * Palabra: un clic en un versículo lo pone al aire. Su capítulo queda en Programa (una sola vez,
   * con su versión) y se va a la diapositiva de ese versículo; las flechas siguen desde ahí.
   */
  async function projectScripture(chapter: string, translation: string | null, slide: number) {
    if (!p) throw new Error('Espera a que cargue el programa.');
    let item = p.itemsList.find((i) => i.kind === 'scripture' && i.passage === chapter && (i.translation ?? null) === translation);
    if (!item) {
      const title = `${chapter}${translation ? ` (${translation})` : ''}`.slice(0, 120);
      const next = await api.put<PresentationDetail>(base, { title: p.title, serviceId: p.service?.id ?? null, items: [...p.itemsList.map(input), input({ kind: 'scripture', title, body: null, passage: chapter, resourceId: null, stageNotes: null, translation })] });
      setSaved(next);
      item = next.itemsList[next.itemsList.length - 1];
      if (!item) return;
    }
    setChosen(item.position);
    await send({ action: 'go', item: item.position, slide });
  }

  // Al abrir el culto: los pasajes que los líderes agregaron al orden del culto entran a Programa
  // (una sola vez cada uno; si el operador los quita, no vuelven).
  const syncedRef = useRef<string | null>(null);
  useEffect(() => {
    const serviceId = p?.service?.id;
    if (!p || !serviceId || !churchId || !canOperate || syncedRef.current === p.id) return;
    syncedRef.current = p.id;
    void (async () => {
      const parts = await orderParts(churchId, serviceId);
      const done = importedPassages(p.id);
      const fresh = parts.flatMap((part) => part.passages ?? []).filter((ps) => !done.has(ps.id));
      if (fresh.length === 0) return;
      const missing = fresh.filter((ps) => !p.itemsList.some((i) => i.kind === 'scripture' && i.passage === ps.passage && (i.translation ?? null) === (ps.translation ?? null)));
      try {
        if (missing.length > 0) {
          const extra = missing.map((ps) => input({ kind: 'scripture', title: `${ps.passage}${ps.translation ? ` (${ps.translation})` : ''}`.slice(0, 120), body: null, passage: ps.passage, resourceId: null, stageNotes: null, translation: ps.translation }));
          setSaved(await api.put<PresentationDetail>(base, { title: p.title, serviceId, items: [...p.itemsList.map(input), ...extra] }));
          state.refresh();
        }
        rememberPassages(p.id, fresh.map((ps) => ps.id));
      } catch (e) {
        setError(errorMessage(e));
      }
    })();
  });

  addRef.current = addItem;
  scriptureRef.current = projectScripture;
  backgroundRef.current = setBackground;

  function drop(event: React.DragEvent) {
    event.preventDefault();
    setDropping(false);
    const raw = event.dataTransfer.getData(DRAG_TYPE);
    if (!raw) return;
    try {
      const item = JSON.parse(raw) as NewItem | NewItem[];
      setError(null);
      void addItem(item).catch((e: unknown) => setError(errorMessage(e)));
    } catch {
      // Lo arrastrado no viene de la biblioteca de abajo.
    }
  }

  const live = s ?? ({} as OperatorState);
  const extras = (
    <AvisosPanel
      sections={[
        { key: 'temporizador', label: 'Temporizador', dot: live.timer ? 'amber' : null, node: <NoticeAndTimer churchId={churchId} state={live} send={send} busy={busy} /> },
        { key: 'anuncios', label: 'Anuncios (listón rojo)', dot: live.notice && live.noticeAudience === 'all' ? 'red' : null, node: <Announcements churchId={churchId} state={live} send={send} busy={busy} /> },
        { key: 'musicos', label: 'Mensaje a los músicos', dot: live.stageNotice ? 'amber' : null, node: <MusiciansMessage state={live} send={send} busy={busy} /> },
        {
          key: 'celular',
          label: 'Control desde el celular',
          node: (
            <Card title="Control desde el celular">
              <p className="small muted">Escanea con el celular (con tu sesión de KEORA abierta) para avanzar desde cualquier lugar del templo.</p>
              <div className="remote-link__qr">
                <QrCode value={`${window.location.origin}/app/presentaciones/${presentationId}/control`} label="Código QR del control desde el celular" />
              </div>
              <Link to={`/app/presentaciones/${presentationId}/control`}>Abrir el control aquí</Link>
              <RemoteInvite churchId={churchId} presentationId={presentationId} />
            </Card>
          ),
        },
        { key: 'obs', label: 'Salida para OBS', node: <ObsLinkCard churchId={churchId} presentationId={presentationId} /> },
      ]}
    />
  );

  return (
    <div className="ewc" style={sizes.rootStyle}>
      <AssistBanner churchId={churchId} presentationId={presentationId} />
      {menu.element}
      <header className="ewc__toolbar">
        <Link className="ewc__back" to="/app/presentaciones" aria-label="Volver a la lista de cultos">
          ←
        </Link>
        <h1 className="ewc__title">{p?.title ?? s?.title ?? 'Presentación'}</h1>
        <div className="ewc__tools">
          <Link className="btn btn--ghost" to={`/app/presentaciones/${presentationId}/editar`}>
            <span>Editar</span>
          </Link>
          <button type="button" className="btn btn--ghost" onClick={() => setThemeOpen(true)}>
            <span>Tema</span>
          </button>
        </div>
        {s ? (
          <div className="ewc__onair" role="group" aria-label="Pantalla">
            <button type="button" className={`ewc__big${s.logo && !s.blackout ? ' ewc__big--on' : ''}`} aria-pressed={Boolean(s.logo)} disabled={busy === 'logo'} onClick={() => void send({ action: 'logo', on: !s.logo })}>
              <span className="ewc__big-icon ewc__big-icon--logo" aria-hidden="true" />
              Logo
            </button>
            <button type="button" className={`ewc__big${s.blackout ? ' ewc__big--danger' : ''}`} aria-pressed={s.blackout} disabled={busy === 'blackout'} onClick={() => void send({ action: 'blackout', on: !s.blackout })}>
              <span className="ewc__big-icon ewc__big-icon--black" aria-hidden="true" />
              Negro
            </button>
            <button type="button" className={`ewc__big${s.textHidden ? ' ewc__big--on' : ''}`} aria-pressed={Boolean(s.textHidden)} title="Quita el texto y deja el fondo (tecla C)" disabled={busy === 'textHidden'} onClick={() => void send({ action: 'textHidden', on: !s.textHidden })}>
              <span className="ewc__big-icon ewc__big-icon--clear-text" aria-hidden="true" />
              Ocultar letra
            </button>
            <button type="button" className={`ewc__big${s.stageFull ? ' ewc__big--on' : ''}`} aria-pressed={Boolean(s.stageFull)} title="La pantalla de los músicos muestra solo la letra o el video, en grande" disabled={busy === 'stageFull'} onClick={() => void send({ action: 'stageFull', on: !s.stageFull })}>
              <span className="ewc__big-icon ewc__big-icon--stagefull" aria-hidden="true" />
              Músicos completo
            </button>
            <button type="button" className="ewc__big" title="Baja lo que se está mostrando" disabled={busy === 'clear'} onClick={() => void send({ action: 'clear' })}>
              <span className="ewc__big-icon ewc__big-icon--clear" aria-hidden="true" />
              Pantalla vacía
            </button>
          </div>
        ) : null}
        {s ? <QuickButtons send={send} busy={busy} /> : null}
        <div className="ewc__project">
          <ProjectButtons churchId={churchId} presentationId={presentationId} />
        </div>
      </header>
      {p?.archived ? <Alert tone="info">Archivada: restáurala para proyectarla.</Alert> : null}
      {!s && state.error ? <ErrorState error={state.error} onRetry={state.refresh} /> : null}
      {!s && !state.error ? <Loading /> : null}
      {s && state.error ? <Alert tone="info">Sin conexión con el servidor. Reintentando…</Alert> : null}
      {error ? <Alert>{error}</Alert> : null}
      {s ? (
        <div className="ewc__top" style={sizes.topStyle}>
          <section
            className={`ewc__pane ewc__schedule${dropping ? ' ewc__schedule--drop' : ''}`}
            aria-label="Programa"
            onDragOver={(e) => {
              if (e.dataTransfer.types.includes(DRAG_TYPE)) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
                setDropping(true);
              }
            }}
            onDragLeave={() => setDropping(false)}
            onDrop={drop}
          >
            <h2 className="ewc__pane-title">Programa</h2>
            {detail.status === 'error' ? <ErrorState error={detail.error} onRetry={detail.reload} /> : null}
            {p && items.length === 0 ? <p className="small muted">El programa está vacío. Arrastra aquí cantos, textos de la Palabra, videos o imágenes desde abajo.</p> : null}
            <ol className="ewc__items">
              {items.map((item) => {
                const isLive = s.liveCursor?.item === item.position;
                const isSel = selected?.position === item.position;
                return (
                  <li key={item.position}>
                    <button
                      type="button"
                      className={`ewc__item${isSel ? ' ewc__item--selected' : ''}${isLive ? ' ewc__item--live' : ''}`}
                      aria-pressed={isSel}
                      onClick={() => setChosen(item.position)}
                      onContextMenu={(e) =>
                        menu.open(e, item.title, [
                          { label: 'Proyectar', onSelect: () => void send({ action: 'go', item: item.position, slide: 0 }) },
                          { label: 'Editar programa…', onSelect: () => navigate(`/app/presentaciones/${presentationId}/editar`) },
                          { label: 'Quitar del programa', danger: true, onSelect: () => window.confirm(`¿Quitar «${item.title}» del programa?`) && void removeItem(item.position) },
                        ])
                      }
                      onDoubleClick={() =>
                        void send({
                          action: 'go',
                          item: item.position,
                          slide: 0,
                        })
                      }
                    >
                      <span className="ewc__item-kind">{KIND_LABEL[item.kind]}</span>
                      <span className="ewc__item-title">{item.title}</span>
                      {isLive ? <span className="ewc__item-live">En pantalla</span> : null}
                    </button>
                  </li>
                );
              })}
            </ol>
          </section>
          <Splitter orientation="vertical" label="Ancho de Programa" {...sizes.column('c1')} />
            <section className="ewc__pane ewc__slides" aria-label="Diapositivas">
              <div className="ewc__pane-head">
                <h2 className="ewc__pane-title">{selected ? selected.title : 'Diapositivas'}</h2>
                <button
                  type="button"
                  className="ewc__viewtoggle"
                  onClick={() => setSlideView(slideView === 'list' ? 'cards' : 'list')}
                  title={slideView === 'list' ? 'Ver en tarjetas' : 'Ver en lista'}
                  aria-label={slideView === 'list' ? 'Ver en tarjetas' : 'Ver en lista'}
                >
                  <span aria-hidden="true">{slideView === 'list' ? '▦' : '☰'}</span>
                </button>
              </div>
              {selected?.stageNotes ? <p className="small ewc__notes">Notas: {selected.stageNotes}</p> : null}
              <div className={`program__slides ewc__slide-grid${slideView === 'list' ? ' ewc__slide-grid--list' : ''}`}>
                {selected?.slides.map((slide, i) => {
                  const isLive = s.liveCursor?.item === selected.position && s.liveCursor.slide === i;
                  const isPreview = s.previewCursor?.item === selected.position && s.previewCursor.slide === i;
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`program__slide ewc__slide${isLive ? ' program__slide--live' : ''}${isPreview ? ' program__slide--preview' : ''}`}
                      aria-current={isLive ? 'true' : undefined}
                      onClick={() =>
                        send({
                          action: 'preview',
                          item: selected.position,
                          slide: i,
                        })
                      }
                      onDoubleClick={() =>
                        send({
                          action: 'go',
                          item: selected.position,
                          slide: i,
                        })
                      }
                    >
                      {slide.label ? <span className="program__label">{slide.label}</span> : null}
                      <span className="program__slide-text">{(slideView === 'list' ? slide.text : firstLines(slide.text)) ?? slide.caption ?? selected.resource?.name ?? selected.title}</span>
                      {isLive ? <span className="visually-hidden"> (en pantalla)</span> : null}
                    </button>
                  );
                })}
              </div>
              <p className="small muted ewc__hint">Clic: a «Siguiente» · Doble clic: mostrar · Teclado: → siguiente, ← anterior, Enter mostrar, B negro, L logo, C ocultar letra</p>
            </section>
          <Splitter orientation="vertical" label="Ancho de las diapositivas" {...sizes.column('c2')} />
          <section className="ewc__pane ewc__monitors" aria-label="En pantalla">
            <div className="console__controls">
              <Button variant="secondary" busy={busy === 'previous'} onClick={() => send({ action: 'previous' })}>
                ◀ Anterior
              </Button>
              <Button busy={busy === 'take'} onClick={() => send({ action: 'take' })}>
                Mostrar
              </Button>
              <Button variant="secondary" busy={busy === 'next'} onClick={() => send({ action: 'next' })}>
                Siguiente ▶
              </Button>
            </div>
            <figure className="monitor monitor--live">
              <figcaption>
                En pantalla {s.blackout ? <span className="badge badge--warn">Negro</span> : null}
                {s.logo && !s.blackout ? <span className="badge">Logo</span> : null}
                {s.countdown && !s.blackout ? <span className="badge">Conteo</span> : null}
              </figcaption>
              <LiveMonitor state={s} fileUrl={fileUrl} onInfo={setVideoInfo} />
              {isVideoSlide(s.live) && !s.blackout && !s.logo ? <PlaybackBar playback={s.playback} serverTime={s.serverTime} info={videoInfo} send={(c) => void send(c)} /> : null}
            </figure>
            {s.liveBackground ? (
              <p className="ewc__bgline">
                <span>
                  Fondo: <strong>{s.liveBackground.resource?.name ?? s.liveBackground.localFile?.name ?? (s.liveBackground.camera ? (loadSources().find((x) => x.id === s.liveBackground?.camera)?.name ?? 'Cámara') : '')}</strong>
                </span>
                <Button variant="ghost" busy={busy === 'background'} onClick={() => void setBackground(null)}>
                  Quitar fondo
                </Button>
              </p>
            ) : null}
          </section>
        </div>
      ) : null}
      {s ? <Splitter orientation="horizontal" label="Alto de la parte de arriba" {...sizes.top} /> : null}
      {themeOpen ? (
        <div className="scripture-modal" onClick={() => setThemeOpen(false)}>
          <div className="scripture-modal__box card theme-modal" role="dialog" aria-modal="true" aria-label="Tema de las pantallas" onClick={(e) => e.stopPropagation()}>
            <div className="theme-modal__head">
              <h2>Tema de las pantallas</h2>
              <Button variant="ghost" onClick={() => setThemeOpen(false)}>
                Cerrar
              </Button>
            </div>
            <ThemeEditor onSaved={() => state.refresh()} />
          </div>
        </div>
      ) : null}
      {s ? <ConsoleLibrary churchId={churchId} onAdd={onAdd} onBackground={onBackground} onScripture={onScripture} extras={extras} /> : null}
    </div>
  );
}

/** Lo que ve la congregación: la diapositiva o, antes del culto, el conteo en grande. */
export function LiveMonitor({ state: s, fileUrl, onInfo }: { state: OperatorState; fileUrl: (rid: string) => string; onInfo?: (info: { current: number; duration: number }) => void }) {
  const countdown = useCountdown(s.timer?.endsAt ?? null, s.serverTime);
  if (s.countdown && countdown && !s.blackout) return <CountdownView label={s.timer?.label ?? null} time={countdown} theme={s.theme} logo={s.brand ?? { resourceId: null, name: s.title }} fileUrl={fileUrl} />;
  // En la consola el video en vivo se ve de verdad (sin sonido) y de él salen el tiempo y la duración.
  return <SlideView slide={s.live} fileUrl={fileUrl} blackout={s.blackout} label="En pantalla" theme={s.theme} logo={s.logo} background={s.liveBackground} hideText={s.textHidden} player={onInfo ? { playback: s.playback, serverTime: s.serverTime, muted: true, onInfo } : undefined} />;
}

export const QUICK: { name: string; label: string; hint: string }[] = [
  {
    name: 'antes',
    label: 'Antes del culto',
    hint: 'Conteo en grande hasta la hora del culto',
  },
  {
    name: 'empezar',
    label: 'Empezar',
    hint: 'Quita el conteo y muestra lo primero',
  },
  {
    name: 'predicacion',
    label: 'Predicación',
    hint: '40 min para el predicador y la lectura lista',
  },
  { name: 'terminar', label: 'Terminar', hint: 'Quita todo y deja el logo' },
];

/** Botones rápidos en la barra de arriba: cada uno hace varias cosas a la vez, sin configurar nada. */
function QuickButtons({ send, busy }: { send: (c: Command) => Promise<void>; busy: string | null }) {
  return (
    <div className="ewc__onair ewc__quick" role="group" aria-label="Botones rápidos">
      {QUICK.map((q) => (
        <button key={q.name} type="button" className="ewc__big ewc__big--text" title={q.hint} disabled={busy !== null} onClick={() => void send({ action: 'quick', name: q.name })}>
          {q.label}
        </button>
      ))}
    </div>
  );
}

function NoticeAndTimer({ churchId, state: s, send, busy }: { churchId: string; state: OperatorState; send: (c: Command) => Promise<void>; busy: string | null }) {
  const countdown = useCountdown(s.timer?.endsAt ?? null, s.serverTime);
  const audience = (form: FormData): Audience => (form.get('audience') === 'all' ? 'all' : 'stage');

  function timer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const minutes = Number(form.get('minutes'));
    if (minutes >= 1)
      void send({
        action: 'timer',
        minutes,
        label: String(form.get('label') ?? '').trim() || null,
        audience: audience(form),
      });
  }

  const audienceSelect = (id: string, value: Audience) => (
    <select id={id} name="audience" defaultValue={value} aria-label="Quién lo ve">
      <option value="stage">Solo escenario</option>
      <option value="all">También la congregación</option>
    </select>
  );

  return (
    <>
      <Card title="Temporizador">
        {countdown ? (
          <p className="console__current">
            <strong className="countdown">{countdown}</strong> <span className="muted small">{s.timer?.label ?? ''}</span>
          </p>
        ) : null}
        <form className="form" onSubmit={timer}>
          <div className="console__timer">
            <label className="visually-hidden" htmlFor="minutes">
              Minutos
            </label>
            <input id="minutes" name="minutes" type="number" min={1} max={240} defaultValue={5} inputMode="numeric" />
            <label className="visually-hidden" htmlFor="timer-label">
              Para qué
            </label>
            <input id="timer-label" name="label" maxLength={60} placeholder="Predicación" />
          </div>
          {audienceSelect('timer-audience', s.timerAudience)}
          <div className="actions actions--wrap">
            <Button type="submit" variant="secondary" busy={busy === 'timer'}>
              Iniciar
            </Button>
            {s.timer ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  send({
                    action: 'timer',
                    minutes: null,
                    label: null,
                    audience: s.timerAudience,
                  })
                }
              >
                Detener
              </Button>
            ) : null}
          </div>
        </form>
      </Card>
    </>
  );
}

/** Veces que pasa un listón antes de cerrarse solo (o «siempre»); se recuerda en esta computadora. */
function useRepeat(key: string, initial: string): [string, (v: string) => void] {
  const [repeat, setRepeat] = useState<string>(() => {
    try {
      return localStorage.getItem(key) ?? initial;
    } catch {
      return initial;
    }
  });
  const pick = (v: string) => {
    setRepeat(v);
    try {
      localStorage.setItem(key, v);
    } catch {
      // Sin almacenamiento: solo no se recuerda.
    }
  };
  return [repeat, pick];
}

const repeatValue = (v: string) => (v === 'siempre' ? null : Number(v));
const repeatLabel = (n: number) => (n === 1 ? '1 vez' : `${n} veces`);

function RepeatSelect({ value, onChange, label = 'Veces que pasa' }: { value: string; onChange: (v: string) => void; label?: string }) {
  return (
    <label className="announce__repeat">
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {Array.from({ length: 10 }, (_, i) => String(i + 1)).map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
        <option value="siempre">Siempre</option>
      </select>
    </label>
  );
}

/**
 * Anuncios del listón rojo de arriba: los que se repiten se guardan y se mandan con un clic; uno
 * esporádico se escribe y se muestra sin guardarlo. «Quitar» lo baja de la pantalla.
 */
function Announcements({ churchId, state: s, send, busy }: { churchId: string; state: OperatorState; send: (c: Command) => Promise<void>; busy: string | null }) {
  const saved = useAsync((signal) => api.get<{ items: Announcement[] }>(`/presentation/churches/${churchId}/announcements`, signal).then((r) => r.items), [churchId]);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const showing = s.noticeAudience === 'all' ? s.notice : null;
  const [repeat, pickRepeat] = useRepeat('keora.consola.vecesAviso', '3');
  const show = (t: string) => send({ action: 'notice', text: t, audience: 'all', repeat: repeatValue(repeat) });

  async function save() {
    const t = text.trim();
    if (!t) return;
    setError(null);
    try {
      await api.post(`/presentation/churches/${churchId}/announcements`, { text: t });
      setText('');
      saved.reload();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  async function remove(a: Announcement) {
    if (!window.confirm(`¿Borrar el anuncio «${a.text.slice(0, 60)}»?`)) return;
    try {
      await api.delete(`/presentation/churches/${churchId}/announcements/${a.id}`);
      saved.reload();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  return (
    <Card title="Anuncios (listón rojo)">
      {showing ? (
        <div className="announce__now">
          <span className="announce__dot" aria-hidden="true" />
          <strong>{showing}</strong>
          {s.noticeRepeat ? <span className="small muted">({repeatLabel(s.noticeRepeat)})</span> : null}
          <Button type="button" variant="ghost" onClick={() => send({ action: 'notice', text: null, audience: 'all' })}>
            Quitar
          </Button>
        </div>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) void show(text.trim());
        }}
      >
        <label className="visually-hidden" htmlFor="announce-text">
          Anuncio
        </label>
        <textarea id="announce-text" rows={2} maxLength={300} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ej.: Este sábado ayuno congregacional a las 7:00 p. m." />
        <RepeatSelect value={repeat} onChange={pickRepeat} />
        <div className="actions actions--wrap">
          <Button type="submit" variant="secondary" busy={busy === 'notice'} disabled={!text.trim()}>
            Mostrar sin guardar
          </Button>
          <Button type="button" variant="ghost" disabled={!text.trim()} onClick={() => void save()}>
            Guardar
          </Button>
        </div>
      </form>
      {saved.status === 'ready' && saved.data.length > 0 ? (
        <ul className="announce__list" aria-label="Anuncios guardados">
          {saved.data.map((a) => (
            <li key={a.id} className={`announce__item${showing === a.text ? ' announce__item--on' : ''}`}>
              <span className="announce__text">{a.text}</span>
              <button type="button" className="link-button" onClick={() => void show(a.text)}>
                Mostrar<span className="visually-hidden"> {a.text}</span>
              </button>
              <button type="button" className="link-button announce__del" onClick={() => void remove(a)} aria-label={`Borrar ${a.text}`}>
                ✕
              </button>
            </li>
          ))}
        </ul>
      ) : saved.status === 'ready' ? (
        <p className="small muted">Guarda los anuncios que se repiten para mandarlos con un clic.</p>
      ) : null}
    </Card>
  );
}

/** Mensaje que solo ven los músicos, abajo en la pantalla del escenario. */
function MusiciansMessage({ state: s, send, busy }: { state: OperatorState; send: (c: Command) => Promise<void>; busy: string | null }) {
  const [text, setText] = useState('');
  // Por omisión se queda hasta «Quitar», como siempre ha sido.
  const [repeat, pickRepeat] = useRepeat('keora.consola.vecesMusicos', 'siempre');
  return (
    <Card title="Mensaje a los músicos">
      {s.stageNotice ? (
        <div className="announce__now announce__now--stage">
          <span className="announce__dot" aria-hidden="true" />
          <strong>{s.stageNotice}</strong>
          {s.stageNoticeRepeat ? <span className="small muted">({repeatLabel(s.stageNoticeRepeat)})</span> : null}
          <Button type="button" variant="ghost" onClick={() => send({ action: 'stageNotice', text: null })}>
            Quitar
          </Button>
        </div>
      ) : null}
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          const t = text.trim();
          if (!t) return;
          void send({ action: 'stageNotice', text: t, repeat: repeatValue(repeat) }).then(() => setText(''));
        }}
      >
        <label className="visually-hidden" htmlFor="stage-text">
          Mensaje a los músicos
        </label>
        <input id="stage-text" maxLength={300} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ej.: Repitan el coro una vez más" />
        <RepeatSelect value={repeat} onChange={pickRepeat} />
        <p className="small muted">Solo aparece en la pantalla del escenario; la congregación no lo ve.</p>
        <div className="actions">
          <Button type="submit" variant="secondary" busy={busy === 'stageNotice'} disabled={!text.trim()}>
            Enviar a los músicos
          </Button>
        </div>
      </form>
    </Card>
  );
}

const SLIDE_VIEW_KEY = 'keora.consola.diapositivas';

/** Diapositivas en tarjetas o en lista (se recuerda en esta computadora). */
function useSlideView(): ['cards' | 'list', (v: 'cards' | 'list') => void] {
  const [view, setView] = useState<'cards' | 'list'>(() => {
    try {
      return localStorage.getItem(SLIDE_VIEW_KEY) === 'list' ? 'list' : 'cards';
    } catch {
      return 'cards';
    }
  });
  const change = (v: 'cards' | 'list') => {
    setView(v);
    try {
      localStorage.setItem(SLIDE_VIEW_KEY, v);
    } catch {
      // Sin almacenamiento: vale solo mientras siga abierta.
    }
  };
  return [view, change];
}
